<template>
    <div>

        <Head :title="displayName" />
        <div class="flex justify-start mb-8 max-w-3xl">
            <h1 class="text-3xl font-bold">
                <Link class="text-orange-400 hover:text-orange-600" href="/users">Users</Link>
                <span class="text-orange-400 font-medium">/</span>
                {{ displayName }}
            </h1>
            <img v-if="user.photo" class="block ml-4 w-8 h-8 rounded-full" :src="user.photo" />
        </div>
        <trashed-message v-if="user.deleted_at" class="mb-6" @restore="restore"> This user has been deleted.
        </trashed-message>
        <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
            <form @submit.prevent="update">
                <div class="flex flex-wrap -mb-8 -mr-6 p-8">
                    <text-input v-model="form.first_name" :error="form.errors.first_name"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="First name" />
                    <text-input v-model="form.last_name" :error="form.errors.last_name"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Last name" />
                    <text-input v-model="form.email" :error="form.errors.email" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Email" />
                    <text-input v-model="form.password" :error="form.errors.password" class="pb-8 pr-6 w-full lg:w-1/2"
                        type="password" autocomplete="new-password" label="Password" />
                    <select-input-function v-model="form.owner" :error="form.errors.owner"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Is Admin">
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                    </select-input-function>

                    <file-input v-model="form.photo" :error="form.errors.photo" class="pb-8 pr-6 w-full lg:w-1/2"
                        type="file" accept="image/*" label="Photo" />
                </div>
                <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
                    <button v-if="!user.deleted_at" class="text-red-600 hover:underline" tabindex="-1" type="button"
                        @click="destroy">Delete User</button>
                    <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">Update
                        User</loading-button>
                </div>
            </form>

        </div>
    </div>
</template>

<script>
    import axios from 'axios';
    import { Head, Link, useForm } from '@inertiajs/inertia-vue3'
    import { ref, toRefs, inject, onMounted, watch, computed } from 'vue'
    import Layout from '@/Shared/Layout'
    import TextInput from '@/Shared/TextInput'
    import SelectInput from '@/Shared/SelectInput'
    import SelectInputFunction from '@/Shared/SelectInputFunction'
    import LoadingButton from '@/Shared/LoadingButton'
    import StatusToogleButton from '@/Shared/StatusToogleButton'
    import Breadcrumbs from '@/Shared/Breadcrumbs'
    import ContentHeaderSection from "@/Shared/ContentHeaderSection"
    import InputTag from '@/Shared/InputTag'
    import DeleteIcon from '@/Shared/DeleteIcon'
    import Button from '@/Shared/Button'
    import FileInput from '@/Shared/FileInput'

    export default {
        components: {
            Head,
            Link,
            LoadingButton,
            SelectInput,
            TextInput,
            Breadcrumbs,
            StatusToogleButton,
            Button,
            ContentHeaderSection,
            FileInput,
            DeleteIcon,
            SelectInputFunction
        },
        layout: Layout,
        props: {
            user: Object,
        },
        setup(props) {
            const { user } = toRefs(props)
            const swal = inject('$swal')
            const title = ref(user.value.first_name ?? user.value.name ?? '')

            const form = useForm({
                id: user.value.id,
                _method: 'put',
                first_name: user.value.first_name ?? (user.value.name ? user.value.name.split(' ')[0] : ''),
                last_name: user.value.last_name ?? (user.value.name ? user.value.name.split(' ').slice(1).join(' ') : ''),
                email: user.value.email,
                password: '',
                owner: typeof user.value.owner !== 'undefined' ? user.value.owner : (user.value.is_admin ?? false),
                photo: null,
            })

            const displayName = computed(() => {
                const first = form.first_name || ''
                const last = form.last_name || ''
                const combined = `${first} ${last}`.trim()
                return combined !== '' ? combined : (user.value.name || '')
            })

            const update = () => {
                swal({
                    title: 'Are you sure?',
                    text: "You want to proceed with this action",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                }).then((result) => {
                    if (result.isConfirmed) {
                        form.post(`/users/${user.value.id}`, { forceFormData: true })
                        title.value = displayName.value
                        swal.fire(
                            'Updated!',
                            'Your information has been Updated.',
                            'success'
                        )
                    }
                })
            }

            const destroy = () => {
                swal({
                    title: 'Are you sure?',
                    text: "Do you really want to delete this!",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, delete it!'
                }).then((result) => {
                    if (result.isConfirmed) {
                        form.delete(`/users/${user.value.id}`)
                        swal.fire(
                            'Deleted!',
                            'This user has been deleted.',
                            'success'
                        )
                    }
                })
            }

            const restore = () => {
                swal({
                    title: 'Are you sure?',
                    text: "You want to restore this!",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, Restore it!'
                }).then((result) => {
                    if (result.isConfirmed) {
                        form.put(`/users/${user.value.id}/restore`)
                        swal.fire(
                            'Restored!',
                            'This user has been Restored.',
                            'success'
                        )
                    }
                })
            }

            return {
                user,
                form,
                title,
                displayName,
                update,
                destroy,
                restore,
                swal,
            }
        }
    }
</script>
